import { clamp } from '@/lib/frieze/math'

/**
 * Liquid-glass loupe displacement map (§13).
 *
 * Models the loupe as a glass slab whose top surface is flat across the middle and
 * curves down through a rim band (a convex squircle bevel). A straight-down ray is
 * refracted at the surface with Snell's law (air n=1 → glass n=1.5) and the lateral
 * entry→exit offset is stored as R = x-offset, G = y-offset around a neutral 128.
 *
 * Baked once, client-side, into a data URL consumed by `feImage`.
 */

const SIZE = 75
const RADIUS = 37.5
const BEZEL = RADIUS * 0.86
const THICKNESS = 30
const GLASS_IOR = 1.5

/** feDisplacementMap scale: displacement = SCALE * (channel/255 - 0.5). */
export const LENS_MAP_SCALE = 48

/** Peak lateral offset in px that the encoding can express. */
const MAX_OFFSET = LENS_MAP_SCALE / 2

/** Per-channel spread that produces the rim chromatic fringing (§13, chroma 2.6). */
export const LENS_CHROMA = 2.6

let cached: string | null = null

export const bakeLensMap = (): string | null => {
    if (typeof document === 'undefined') return null
    if (cached) return cached

    const canvas = document.createElement('canvas')
    canvas.width = SIZE
    canvas.height = SIZE

    const ctx = canvas.getContext('2d')
    if (!ctx) return null

    const image = ctx.createImageData(SIZE, SIZE)
    const data = image.data

    for (let y = 0; y < SIZE; y++) {
        for (let x = 0; x < SIZE; x++) {
            const px = x + 0.5 - RADIUS
            const py = y + 0.5 - RADIUS
            const dist = Math.hypot(px, py)

            let ox = 0
            let oy = 0

            if (dist <= RADIUS && dist > BEZEL && dist > 0) {
                const u = clamp((dist - BEZEL) / (RADIUS - BEZEL), 0, 1)

                // Convex squircle bevel: flat at the bezel, falling away to vertical at the rim.
                const slope = Math.min(u / Math.sqrt(Math.max(1e-4, 1 - u * u)), 8)

                const theta1 = Math.atan(slope)
                const theta2 = Math.asin(clamp(Math.sin(theta1) / GLASS_IOR, -1, 1))
                const offset = Math.min((Math.tan(theta1) - Math.tan(theta2)) * THICKNESS, MAX_OFFSET)

                ox = -(px / dist) * offset
                oy = -(py / dist) * offset
            }

            const i = (y * SIZE + x) * 4

            data[i] = clamp(Math.round(128 + (ox / MAX_OFFSET) * 127), 0, 255)
            data[i + 1] = clamp(Math.round(128 + (oy / MAX_OFFSET) * 127), 0, 255)
            data[i + 2] = 128
            data[i + 3] = 255
        }
    }

    ctx.putImageData(image, 0, 0)

    try {
        cached = canvas.toDataURL('image/png')
    } catch {
        return null
    }

    return cached
}

/** Chromium is the only engine that resolves an SVG filter inside `backdrop-filter`. */
export const supportsGlassBackdrop = (): boolean => {
    if (typeof window === 'undefined' || typeof CSS === 'undefined' || !CSS.supports) return false

    return (
        CSS.supports('backdrop-filter', 'url(#fz-lens-glass)') ||
        CSS.supports('-webkit-backdrop-filter', 'url(#fz-lens-glass)')
    )
}
